import { addShortcodeToEditor, getShortcodeWrapper } from "@/assets/utilities/tinymce/shortodeAddition";
import { getMultiStepFormTemplate } from "@/assets/utilities/tinymce/shortcodes/templates";
import { parseToHtml } from "@/utility/parseToHtml";
import { serializeDynamicComponents, visualizeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
import { convertAssetsPathToRelativeLinks } from "@/utility/convertAssetsPaths";
import { traverseTree } from "@/utility/traverseTree";
import { findInvalidTelHrefsDeep } from "@/utility/validateLiveChatTelHrefs";
import { showTelConfirmModal } from "@/assets/utilities/tinymce/helpers/telConfirmModal";
import i18n from "@/i18n";

const TRANSFORMABLE_KEYS = ["content", "text"];

function transformContent(obj, transformFn) {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => transformContent(item, transformFn));
  }

  const newObj = {};
  Object.keys(obj).forEach(key => {
    if (TRANSFORMABLE_KEYS.includes(key)) {
      const content = parseToHtml(obj[key]);
      transformFn(content);
      newObj[key] = content.body.innerHTML;
    } else {
      newObj[key] = transformContent(obj[key], transformFn);
    }
  });

  return newObj;
}

function convertTreeAssetPaths(pages) {
  traverseTree(pages, page => {
    if (page.content) {
      page.content = convertAssetsPathToRelativeLinks(page.content)?.body?.innerHTML;
    }
    if (page.text) {
      page.text = convertAssetsPathToRelativeLinks(page.text)?.body?.innerHTML;
    }
    if (page.value) {
      page.value = convertAssetsPathToRelativeLinks(page.value)?.body?.innerHTML;
    }
  });
}

function encodeFormData(data) {
  convertTreeAssetPaths(data.pages);
  const serializedData = transformContent(data, serializeDynamicComponents);
  return btoa(unescape(encodeURIComponent(JSON.stringify(serializedData))));
}

const dialogButtons = [
  { type: "cancel", name: "close", text: "Cancel" },
  { type: "custom", name: "submit", text: "Submit", primary: true },
];

const editMultiStepFormConfig = (editor, shortcodeWrapper) => {
  let currentData = null;

  const multiStepFormWindow = editor.windowManager.openUrl({
    title: "Multi-Step Form",
    url: "/multi-step-form-dialog",
    buttons: dialogButtons,
    onAction: function (instance) {
      const save = () => {
        shortcodeWrapper.setAttribute("data-multi-step-form-data", encodeFormData(currentData));
        editor.fire("change");
        instance.close();
      };

      const { valid, errors } = findInvalidTelHrefsDeep(currentData);
      if (!valid) {
        showTelConfirmModal(
          i18n.t("shortcodes.live_chat.invalid_tel_href_confirm", { count: errors.length }),
          errors.map(e => e.invalidHrefs.join(", ")).join(", "),
        ).then(confirmed => {
          if (confirmed) save();
          else instance.sendMessage({ type: "telHrefValidationFailed" });
        });
        return;
      }

      save();
    },
    onMessage: (instance, message) => {
      if (message.mceAction === "requestData") {
        sendData();
      }
      if (message.mceAction === "updateMultiStepFormData") {
        currentData = message.data;
      }
    },
    width: window.innerWidth - 20,
    height: window.innerHeight - 20,
  });

  const sendData = () => {
    const multiStepFormShortcodeData = shortcodeWrapper.getAttribute("data-multi-step-form-data");
    const parsedData = JSON.parse(decodeURIComponent(escape(window.atob(multiStepFormShortcodeData))));
    const content = transformContent(parsedData, visualizeDynamicComponents);

    multiStepFormWindow.sendMessage({
      type: "sendData",
      data: content,
    });
  };
};

const multiStepFormConfig = (tinymce, uuid, shortcodeOnClick) => {
  let currentData = null;

  tinymce.activeEditor.windowManager.openUrl({
    title: "Multi-Step Form",
    url: "/multi-step-form-dialog?data=null",
    buttons: dialogButtons,
    onAction: function (instance) {
      const save = () => {
        const shortcodeWrapper = getShortcodeWrapper(
          uuid,
          shortcodeOnClick,
          getMultiStepFormTemplate(),
          "multi-step-form-shortcode",
        );

        const shortcodeWrapperEl = parseToHtml(shortcodeWrapper).querySelector(".shortcode");
        shortcodeWrapperEl.setAttribute("data-multi-step-form-data", encodeFormData(currentData));

        addShortcodeToEditor(shortcodeWrapperEl.outerHTML, uuid);
        instance.close();
      };

      const { valid, errors } = findInvalidTelHrefsDeep(currentData);
      if (!valid) {
        showTelConfirmModal(
          i18n.t("shortcodes.live_chat.invalid_tel_href_confirm", { count: errors.length }),
          errors.map(e => e.invalidHrefs.join(", ")).join(", "),
        ).then(confirmed => {
          if (confirmed) save();
          else instance.sendMessage({ type: "telHrefValidationFailed" });
        });
        return;
      }

      save();
    },
    onMessage: (instance, message) => {
      if (message.mceAction === "updateMultiStepFormData") {
        currentData = message.data;
      }
    },
    width: window.innerWidth - 20,
    height: window.innerHeight - 20,
  });
};

export { multiStepFormConfig, editMultiStepFormConfig };
